import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight, X } from 'lucide-react';
import Aide from './Aide';
import { AIDES_BLOCS } from '../utils/aides';

/**
 * Section de l'écran des paramètres : une carte fermée, une modale ouverte.
 *
 * Les sections se dépliaient en place, et c'était le déplié qui posait
 * problème : la messagerie ou la facturation font trente champs, et une
 * section ouverte repoussait tout le reste de la colonne hors de l'écran —
 * on réglait un champ en ayant perdu de vue où l'on était.
 *
 * Ouverte, la section prend donc le premier plan, en grand, seule. Fermée —
 * clic hors de la modale, croix rouge, ou Échap — elle reprend sa place de
 * carte dans la grille, qui n'a pas bougé pendant ce temps.
 */
export default function SectionParametres({
  titre,
  description,
  icone: Icone,
  ouverteParDefaut = false,
  signalOuverture = 0,
  actions,
  children,
}: {
  titre: string;
  description?: string;
  icone: any;
  ouverteParDefaut?: boolean;
  /**
   * Ordre d'ouverture venu du parent : chaque incrément ouvre la section.
   * La bannière des paramètres manquants s'en sert pour mener au champ.
   */
  signalOuverture?: number;
  /**
   * Commandes propres à la section, posées à côté du titre sur la carte.
   *
   * Elles restent atteignables section fermée : une vérification qu'il faut
   * ouvrir la section pour lancer n'est pas une vérification qu'on pense à
   * faire.
   */
  actions?: ReactNode;
  children: ReactNode;
}) {
  const [ouverte, setOuverte] = useState(ouverteParDefaut);
  const fondRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (signalOuverture > 0) {
      setOuverte(true);
    }
  }, [signalOuverture]);

  // Échap ferme, comme partout ailleurs. L'écouteur ne vit que le temps de
  // la modale : seize sections qui écoutent le clavier en permanence, c'est
  // seize fermetures pour un seul Échap.
  useEffect(() => {
    if (!ouverte) return;

    const surTouche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOuverte(false);
    };
    document.addEventListener('keydown', surTouche);

    // La page ne défile plus sous la modale : deux ascenseurs qui répondent
    // à la même molette, c'est celui qu'on ne regarde pas qui bouge.
    const debordement = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', surTouche);
      document.body.style.overflow = debordement;
    };
  }, [ouverte]);

  return (
    <>
      {/* La carte : le titre, l'aide, les actions — et l'ouverture au clic. */}
      <section
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
        aria-label={`Ouvrir ${titre}`}
        onClick={() => setOuverte(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOuverte(true);
          }
        }}
        className="card lg:p-5 h-full flex flex-col cursor-pointer select-none hover:border-primary-300 dark:hover:border-primary-700 border-2 border-transparent transition-colors"
      >
        {/* h-full + colonne : la carte occupe la hauteur de sa rangée, et le
            résumé pousse ce qui suit vers le bas. Sans cela, deux cartes
            voisines dont l'une a un titre sur deux lignes ne s'alignent ni en
            haut ni en bas. */}
        <div className="flex items-start justify-between gap-3 flex-1">
          <div className="min-w-0 flex flex-wrap items-center gap-x-2 content-start">
            <h2 className="font-semibold lg:text-lg text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Icone className="w-5 h-5 text-primary-600 shrink-0" />
              {titre}
            </h2>
            {/* Le « i » à côté du titre : ce que fait ce bloc, pour qui
                découvre l'écran. Son clic n'ouvre pas la modale. */}
            {AIDES_BLOCS[titre] && (
              <Aide titre={titre} taille="bloc">
                {AIDES_BLOCS[titre]}
              </Aide>
            )}
            {description && (
              <p className="basis-full text-sm text-gray-600 dark:text-gray-400 mt-1">{description}</p>
            )}
          </div>

          {actions && (
            <div
              className="shrink-0"
              // Le clic sur une action ne doit pas ouvrir la modale.
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              {actions}
            </div>
          )}

          <span className="shrink-0 p-1.5 text-gray-400" aria-hidden="true">
            <ChevronRight className="w-5 h-5" />
          </span>
        </div>
      </section>

      {/* La modale, par portail : sortie du flux de la grille, elle ne peut
          être ni rognée par un overflow ni coincée sous une carte voisine. */}
      {ouverte &&
        createPortal(
          <div
            ref={fondRef}
            role="dialog"
            aria-modal="true"
            aria-label={titre}
            onMouseDown={(e) => {
              // Le clic hors de la modale ferme — mais seulement s'il a
              // commencé sur le fond : une sélection de texte qui déborde du
              // panneau ne doit pas tout refermer.
              if (e.target === fondRef.current) setOuverte(false);
            }}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 sm:p-8"
          >
            <div className="w-full max-w-4xl my-auto bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700">
              <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-5 py-4 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 rounded-t-2xl">
                <h2 className="font-semibold text-lg text-gray-900 dark:text-gray-100 flex items-center gap-2 min-w-0">
                  <Icone className="w-5 h-5 text-primary-600 shrink-0" />
                  <span className="truncate">{titre}</span>
                </h2>
                <button
                  type="button"
                  onClick={() => setOuverte(false)}
                  aria-label={`Fermer ${titre}`}
                  className="shrink-0 w-8 h-8 grid place-items-center rounded-full bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/40 dark:text-red-300 dark:hover:bg-red-900/60 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="p-5">{children}</div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
